import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  SearchIcon,
  ExclamationIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import { LockClosedIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import Button from "../../../../Components/v2/Button";
import FormDate from "../../../../Components/v2/Form/FormDate";
import { ModalAlert } from "../../../../Components/ModalAlert";
import TableSkeleton from "../../../../Components/TableSkeleton";
import { EmptyList } from "../../../../Components/EmptyList";
import ErrorStateTable from "../../../../Components/ErrorStateTable";
import FormError from "../../../../Components/v2/Form/FormError";

import {
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  addLocalNotification,
  listDeposits,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import DepositModal from "../../../../Components/v2/Accounting/DepositModal";
import { DEPOSIT_ACCOUNT_CODE } from "../../../../Utils/DepositUtil";
import PeriodLockGate from "../../../../Components/v2/Accounting/PeriodLockGate";
import {
  clearUpdateOpeningBalance,
  requestListAccount,
  requestUpdateOpeningBalance,
} from "../../../../Context/accounting/actions";
import { ACCOUNT_CATEGORY_OPTIONS } from "../../../../Constants/accounting";
import { formatCurrency, getErrorMessages } from "../../../../Utils";
import {
  RETAINED_EARNINGS_CODE,
  isProfitLossAccount,
  sortByCategoryOrder,
} from "../../../../Utils/balanceSheet";
import { useNavigate } from "react-router-dom";
import {
  getLastFiscalYearEnd,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../Utils/PeriodUtils";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

//helpers
const getCategoryLabel = (categoryValue) => {
  const cat = ACCOUNT_CATEGORY_OPTIONS.find((c) => c.value === categoryValue);
  return cat ? cat.label : categoryValue;
};

// 220205 Uang Muka Pelanggan is not typed in here: it is the sum of the
// opening balance deposits, each recorded against its customer so it can be
// applied to that customer's invoices later. The row shows that sum, locked,
// with the way to change it.
const DepositAccountRow = ({ account, total, onManage, disabled }) => {
  const { t } = useTranslation();
  return (
    <tr className="bg-gray-50/60" data-testid="opening-deposit-row">
      <td className="px-4 py-2 pl-10 text-sm text-gray-500 whitespace-nowrap font-mono">
        {account.code}
      </td>
      <td className="px-4 py-2 text-sm text-gray-800">
        <span className="block whitespace-nowrap">{account.name}</span>
        <span className="block text-xs text-gray-500">
          {t("deposits.opening.rowHint")} ·{" "}
          <button
            type="button"
            onClick={onManage}
            disabled={disabled}
            className="font-medium text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
          >
            {t("deposits.actions.setPerCustomer")}
          </button>
        </span>
      </td>
      <td className="px-4 py-2 text-right text-sm text-gray-400 w-52">
        <span className="px-2">0</span>
      </td>
      <td className="px-4 py-2 text-right text-sm text-gray-900 w-52">
        <span
          className="inline-flex items-center justify-end gap-2 px-2 font-medium tabular-nums"
          title={t("deposits.opening.locked")}
        >
          <LockClosedIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          <span className="sr-only">{t("deposits.opening.locked")}</span>
          <NumericFormat
            displayType="text"
            value={total || 0}
            thousandSeparator="."
            decimalSeparator=","
          />
        </span>
      </td>
    </tr>
  );
};

const OpeningBalanceV2 = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);

  const { isDateClosed, closedThroughLabel } = usePeriodLock(
    user?.mainBusiness?.id,
    { locale: i18n.language },
  );

  // An opening balance is stated as of the end of the last fiscal year that
  // has ended — 31 December for a January year, 30 June for a July one — and
  // may never be dated later; that is what `maxDate` on the picker enforces.
  // So once the books are closed through that day, *every* selectable date is
  // inside the closed period and there is nothing left to save. That is why
  // this surface gets a gate rather than the usual minDate: constraining the
  // picker would leave a calendar where nothing can be picked, and a form that
  // is guaranteed to fail is not honest. The day is checked from its WIB
  // start, so the laptop's zone cannot move it across the boundary.
  const fiscalYearStartMonth = user?.mainBusiness?.fiscalYearStartMonth;
  const openingDate = useMemo(
    () => getLastFiscalYearEnd(fiscalYearStartMonth),
    [fiscalYearStartMonth],
  );
  const periodLocked = isDateClosed(wibStartOfDay(openingDate));

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const {
    accounts,
    accountError,
    accountLoading,
    transactionID,
    submitLoading,
    submitError,
  } = useAccountingState();

  const [debitValues, setDebitValues] = useState({});
  const [creditValues, setCreditValues] = useState({});
  const [editingCell, setEditingCell] = useState({ id: null, field: null });
  const [searchQuery, setSearchQuery] = useState("");
  const [date, setDate] = useState(openingDate);
  const restoredDateRef = useRef(false);

  // The business can arrive or change its fiscal year after this page opened;
  // follow it, without undoing a date restored below on the first render.
  const openingDateTime = openingDate.getTime();
  const lastOpeningDateRef = useRef(openingDateTime);
  useEffect(() => {
    if (lastOpeningDateRef.current === openingDateTime) return;
    lastOpeningDateRef.current = openingDateTime;
    // A date the user picked before the detour to create 330999 stands.
    if (restoredDateRef.current) {
      restoredDateRef.current = false;
      return;
    }
    setDate(new Date(openingDateTime));
  }, [openingDateTime]);

  // Restore saved form state after returning from COA account creation
  useEffect(() => {
    const saved = sessionStorage.getItem("ob_form_state");
    if (saved) {
      try {
        const { debitValues: d, creditValues: c, date: dt } = JSON.parse(saved);
        if (d) setDebitValues(d);
        if (c) setCreditValues(c);
        if (dt) {
          restoredDateRef.current = true;
          setDate(new Date(dt));
        }
      } catch (_) {}
      sessionStorage.removeItem("ob_form_state");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const initialLoad = useRef(false);

  const [openingDeposits, setOpeningDeposits] = useState([]);
  const [openingDepositsError, setOpeningDepositsError] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);

  const loadOpeningDeposits = async () => {
    if (!user?.mainBusiness?.id) return;
    setOpeningDepositsError(null);
    try {
      const { items } = await listDeposits({ businessID: user.mainBusiness.id });
      setOpeningDeposits(items.filter((deposit) => deposit.opening));
    } catch (error) {
      // Said out loud: on its own the deposit row would read Rp 0.
      setOpeningDeposits([]);
      setOpeningDepositsError(error);
    }
  };

  useEffect(() => {
    loadOpeningDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.mainBusiness?.id]);

  const openingDepositTotal = openingDeposits.reduce(
    (sum, deposit) => sum + (deposit.total || 0),
    0,
  );

  // Validation
  const dateError = submitted && !date;
  const businessError = submitted && !user?.mainBusiness?.id;

  const validateInput = () => !!date && !!user?.mainBusiness?.id;

  //Derived values
  const totalDebit = useMemo(
    () =>
      Object.values(debitValues).reduce(
        (acc, v) => acc + (parseFloat(v) || 0),
        0,
      ),
    [debitValues],
  );

  const totalCredit = useMemo(
    () =>
      Object.values(creditValues).reduce(
        (acc, v) => acc + (parseFloat(v) || 0),
        0,
      ),
    [creditValues],
  );

  const validTotal = totalDebit === totalCredit;
  const difference = formatCurrency(Math.abs(totalDebit - totalCredit));

  // Income statement accounts never carry an opening balance.
  const balanceAccounts = useMemo(
    () => accounts.filter((a) => !isProfitLossAccount(a)),
    [accounts],
  );

  const retainedEarningsAccount = useMemo(
    () => accounts.find((a) => a.code === RETAINED_EARNINGS_CODE),
    [accounts],
  );

  const changedAccounts = useMemo(
    () =>
      balanceAccounts
        .map((a) => ({
          id: a.id,
          debit: parseFloat(debitValues[a.id]) || 0,
          credit: parseFloat(creditValues[a.id]) || 0,
        }))
        .filter((a) => a.debit !== 0 || a.credit !== 0)
        // Set per customer through the deposit form, never from this grid.
        .filter(
          (a) =>
            accounts.find((account) => account.id === a.id)?.code !==
            DEPOSIT_ACCOUNT_CODE,
        ),
    [accounts, balanceAccounts, debitValues, creditValues],
  );

  const openingBalanceAccount = useMemo(
    () => accounts.find((a) => a.code === "330999"),
    [accounts],
  );

  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return balanceAccounts;
    const q = searchQuery.toLowerCase();
    return balanceAccounts.filter((a) => {
      const categoryLabel = getCategoryLabel(a.category);
      const translatedLabel = t(categoryLabel);
      return (
        a.name?.toLowerCase().includes(q) ||
        a.code?.toLowerCase().includes(q) ||
        categoryLabel?.toLowerCase().includes(q) ||
        translatedLabel?.toLowerCase().includes(q)
      );
    });
  }, [balanceAccounts, searchQuery, t]);

  // Groups follow the chart of accounts order, so a liability moved to
  // long-term lists after Other Current Liabilities rather than at its code.
  const groupedAccounts = useMemo(() => {
    const groups = {};
    sortByCategoryOrder(filteredAccounts).forEach((a) => {
      const cat = a.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(a);
    });
    return groups;
  }, [filteredAccounts]);

  const loadAccounts = () => {
    if (!user?.mainBusiness?.id) return;
    requestListAccount(dispatch, {
      page: 1,
      loadAll: true,
      loadAllLoading: true,
      businessID: user.mainBusiness.id,
    });
  };

  useEffect(() => {
    loadAccounts();
    return () => {
      clearUpdateOpeningBalance(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.mainBusiness?.id]);

  // Initialize debit/credit maps when accounts load
  useEffect(() => {
    const d = {};
    const c = {};
    accounts.forEach((a) => {
      d[a.id] = d[a.id] ?? 0;
      c[a.id] = c[a.id] ?? 0;
    });
    setDebitValues((prev) => ({ ...d, ...prev }));
    setCreditValues((prev) => ({ ...c, ...prev }));
    if (accounts.length > 0) initialLoad.current = true;
  }, [accounts]);

  useEffect(() => {
    if (transactionID) {
      setShowConfirmModal(false);
      addLocalNotification(notificationDispatch, {
        id: `opening-balance-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "Your opening balance has been updated.",
        showFull: true,
      });
      clearUpdateOpeningBalance(dispatch);
      navigate("/v2/accounting/coa");
    }
  }, [transactionID, notificationDispatch, dispatch, navigate]);

  useEffect(() => {
    if (submitError) {
      setShowConfirmModal(false);
      setErrorMessage(getErrorMessages(submitError));
      setShowErrorModal(true);
      clearUpdateOpeningBalance(dispatch);
    }
  }, [submitError, dispatch]);

  //Handlers
  const handleDebitChange = (id, value) =>
    setDebitValues((prev) => ({ ...prev, [id]: parseFloat(value) || 0 }));

  const handleCreditChange = (id, value) =>
    setCreditValues((prev) => ({ ...prev, [id]: parseFloat(value) || 0 }));

  const handleCellClick = (id, field) => setEditingCell({ id, field });
  const handleBlur = () => setEditingCell({ id: null, field: null });

  const onSubmit = (force = false) => {
    if (periodLocked) return;
    setSubmitted(true);
    const valid = validateInput();
    if (valid && (validTotal || force)) {
      const input = {
        business_id: user?.mainBusiness?.id,
        // The close of that day in WIB, not the laptop's midnight.
        created_at: wibEndOfDay(date),
        accounts: changedAccounts,
      };
      requestUpdateOpeningBalance(dispatch, { input });
    } else if (dateError || businessError) {
      setShowConfirmModal(false);
    } else if (!validTotal && !openingBalanceAccount) {
      setShowCreateAccountModal(true);
    } else {
      setShowConfirmModal(true);
    }
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9 min-w-0">
        {/* Page Header */}
        <AccountingPageHeader title="accounting.openingBalance" />

        <PeriodLockGate
          locked={periodLocked}
          closedThroughLabel={closedThroughLabel}
        >
        <div className="px-4 py-5 sm:px-6 lg:px-8 space-y-5">
          <FormError
            error={openingDepositsError}
            title="deposits.opening.loadError"
            onRetry={loadOpeningDeposits}
          />
          <div className="flex sm:flex-row flex-col sm:items-center items-start gap-3">
            <div>
              <FormDate
                name="date"
                selected={date}
                onChange={(val) => {
                  setDate(val);
                  setSubmitted(false);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText={t("Select Date")}
                maxDate={openingDate}
                onClear={() => setDate(null)}
                error={dateError ? t("Date is required.") : undefined}
              />
            </div>
            {date && (
              <div className="text-sm text-gray-500 italic">
                {t("Opening Balance Settings Per")}{" "}
                <span className="text-blue-600">
                  {dayjs(date).locale(i18n.language).format("DD MMMM YYYY")}
                </span>
              </div>
            )}
          </div>

          <div
            role="note"
            className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3"
          >
            <InformationCircleIcon
              className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-blue-700">
              {t("openingBalances.balanceSheetOnly")}
              {retainedEarningsAccount && (
                <>
                  {" "}
                  {t("openingBalances.retainedEarnings")}{" "}
                  <strong className="font-semibold">
                    {`${retainedEarningsAccount.code} ${retainedEarningsAccount.name}`}
                  </strong>
                  .
                </>
              )}
            </p>
          </div>

          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SearchIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.trimStart())}
              className="block w-full pl-10 pr-9 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm"
              placeholder={t("searchByAccountNameOrCode")}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Clear</span>
                <svg
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>

          {searchQuery.trim() && (
            <p className="text-sm text-gray-500">
              {filteredAccounts.length}{" "}
              {filteredAccounts.length === 1
                ? t("accountFound")
                : t("accountsFound")}
            </p>
          )}

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("Code")}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("Account")}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-52">
                      {t("Debit")}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-52">
                      {t("Credit")}
                    </th>
                  </tr>
                </thead>

                {accountLoading ? (
                  <TableSkeleton
                    rows={10}
                    columns={4}
                    columnWidths={[
                      { width: "w-16" },
                      { width: "w-40" },
                      { width: "w-24", align: "right" },
                      { width: "w-24", align: "right" },
                    ]}
                  />
                ) : (
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {accounts.length > 0 ? (
                      Object.keys(groupedAccounts).length > 0 ? (
                        Object.keys(groupedAccounts).map((category) => (
                          <Disclosure
                            as={React.Fragment}
                            key={category}
                            defaultOpen
                          >
                            {({ open }) => (
                              <>
                                {/* Category row */}
                                <tr className="bg-gray-100 hover:bg-gray-150">
                                  <td className="px-4 py-2.5" colSpan={1}>
                                    <DisclosureButton className="flex items-center w-full text-left">
                                      {open ? (
                                        <ChevronDownIcon className="h-4 w-4 mr-2 text-gray-500" />
                                      ) : (
                                        <ChevronRightIcon className="h-4 w-4 mr-2 text-gray-500" />
                                      )}
                                      <span className="text-sm font-semibold text-gray-800">
                                        {t(getCategoryLabel(category))}
                                      </span>
                                    </DisclosureButton>
                                  </td>
                                  <td className="px-4 py-2.5" colSpan={3}>
                                    <span className="text-xs text-gray-500">
                                      {groupedAccounts[category].length}{" "}
                                      {groupedAccounts[category].length === 1
                                        ? t("Account")
                                        : t("Accounts")}
                                    </span>
                                  </td>
                                </tr>

                                <DisclosurePanel as={React.Fragment}>
                                  <>
                                    {groupedAccounts[category].map(
                                      (account) =>
                                        account.code === DEPOSIT_ACCOUNT_CODE ? (
                                          <DepositAccountRow
                                            key={account.id}
                                            account={account}
                                            total={openingDepositTotal}
                                            disabled={writeBlocked || !date}
                                            onManage={() => setShowDepositModal(true)}
                                          />
                                        ) : (
                                        <tr
                                          key={account.id}
                                          className="hover:bg-gray-50 transition-colors"
                                        >
                                          <td className="px-4 py-2 pl-10 text-sm text-gray-500 whitespace-nowrap font-mono">
                                            {account.code}
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">
                                            {account.name}
                                          </td>

                                          <td
                                            className="px-4 py-2 text-right text-sm text-gray-700 whitespace-nowrap cursor-pointer w-52 h-12"
                                            onClick={() =>
                                              handleCellClick(
                                                account.id,
                                                "debit",
                                              )
                                            }
                                          >
                                            {editingCell.id === account.id &&
                                            editingCell.field === "debit" ? (
                                              <NumericFormat
                                                type="text"
                                                value={
                                                  debitValues[account.id] || 0
                                                }
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                displayType="input"
                                                isAllowed={(values) =>
                                                  values.floatValue ===
                                                    undefined ||
                                                  values.floatValue <=
                                                    999999999999
                                                }
                                                onValueChange={(v) =>
                                                  handleDebitChange(
                                                    account.id,
                                                    v.value,
                                                  )
                                                }
                                                onBlur={handleBlur}
                                                autoFocus
                                                className="border border-blue-400 rounded px-2 py-1 w-full text-right text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                              />
                                            ) : (
                                              <div
                                                className={`px-2 py-1 rounded text-right hover:bg-blue-50 hover:border hover:border-blue-200 ${
                                                  debitValues[account.id] > 0
                                                    ? "text-gray-900 font-medium"
                                                    : "text-gray-400"
                                                }`}
                                              >
                                                <NumericFormat
                                                  displayType="text"
                                                  value={
                                                    debitValues[account.id] || 0
                                                  }
                                                  thousandSeparator="."
                                                  decimalSeparator=","
                                                />
                                              </div>
                                            )}
                                          </td>

                                          <td
                                            className="px-4 py-2 text-right text-sm text-gray-700 whitespace-nowrap cursor-pointer w-52 h-12"
                                            onClick={() =>
                                              handleCellClick(
                                                account.id,
                                                "credit",
                                              )
                                            }
                                          >
                                            {editingCell.id === account.id &&
                                            editingCell.field === "credit" ? (
                                              <NumericFormat
                                                type="text"
                                                value={
                                                  creditValues[account.id] || 0
                                                }
                                                thousandSeparator="."
                                                decimalSeparator=","
                                                displayType="input"
                                                isAllowed={(values) =>
                                                  values.floatValue ===
                                                    undefined ||
                                                  values.floatValue <=
                                                    999999999999
                                                }
                                                onValueChange={(v) =>
                                                  handleCreditChange(
                                                    account.id,
                                                    v.value,
                                                  )
                                                }
                                                onBlur={handleBlur}
                                                autoFocus
                                                className="border border-blue-400 rounded px-2 py-1 w-full text-right text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                              />
                                            ) : (
                                              <div
                                                className={`px-2 py-1 rounded text-right hover:bg-blue-50 hover:border hover:border-blue-200 ${
                                                  creditValues[account.id] > 0
                                                    ? "text-gray-900 font-medium"
                                                    : "text-gray-400"
                                                }`}
                                              >
                                                <NumericFormat
                                                  displayType="text"
                                                  value={
                                                    creditValues[account.id] ||
                                                    0
                                                  }
                                                  thousandSeparator="."
                                                  decimalSeparator=","
                                                />
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </>
                                </DisclosurePanel>
                              </>
                            )}
                          </Disclosure>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-6">
                            <EmptyList title={t("noAccountsFound")} />
                          </td>
                        </tr>
                      )
                    ) : accountError ? (
                      <tr>
                        <td colSpan={4} className="p-6">
                          <ErrorStateTable
                            asRow={false}
                            error={accountError}
                            onRetry={loadAccounts}
                          />
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={4} className="p-6">
                          <EmptyList title={t("No Accounts")} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}

                {accounts.length > 0 && (
                  <tfoot className="bg-gray-50 sticky bottom-0 border-t-2 border-gray-300">
                    <tr>
                      <td
                        className="px-4 py-3 text-sm font-semibold text-gray-700"
                        colSpan={2}
                      >
                        {t("Total")}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        <NumericFormat
                          displayType="text"
                          value={totalDebit || 0}
                          thousandSeparator="."
                          decimalSeparator=","
                        />
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        <NumericFormat
                          displayType="text"
                          value={totalCredit || 0}
                          thousandSeparator="."
                          decimalSeparator=","
                        />
                      </td>
                    </tr>
                    {!validTotal && (totalDebit > 0 || totalCredit > 0) && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-2 bg-yellow-50 border-t border-yellow-200"
                        >
                          <p className="text-xs text-yellow-800 flex items-center gap-1.5">
                            <ExclamationIcon className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                            {t("debitCreditDifference")}
                            {": "}
                            <span className="font-semibold">{difference}</span>
                          </p>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => onSubmit(false)}
              loading={submitLoading}
              disabled={
                writeBlocked ||
                periodLocked ||
                submitLoading ||
                changedAccounts.length === 0
              }
            >
              {t("Save Changes")}
            </Button>
          </div>
        </div>
        </PeriodLockGate>

        {/*Modals*/}
        {showDepositModal && (
          <DepositModal
            open={showDepositModal}
            opening
            openingDate={date}
            businessID={user?.mainBusiness?.id}
            onClose={() => setShowDepositModal(false)}
            onSaved={(result) => {
              setShowDepositModal(false);
              addLocalNotification(notificationDispatch, {
                id: `opening-deposit-${result?.id || Date.now()}`,
                type: "success",
                title: "common.success",
                body: "deposits.notifications.created",
                showFull: true,
              });
              loadOpeningDeposits();
            }}
          />
        )}
        {/* Error modal */}
        <ErrorDialog
          open={showErrorModal}
          message={errorMessage}
          onClose={() => {
            setShowErrorModal(false);
            setErrorMessage("");
          }}
        />

        {/* Create opening balance account confirmation modal */}
        <ModalAlert
          open={showCreateAccountModal}
          setOpen={setShowCreateAccountModal}
          type="warning"
          title={t("accounts.modal.title.add")}
          description={t("debitCreditCreateAccountConfirm", { difference })}
          confirmButtonText={t("common.create")}
          cancelButtonText={t("common.cancel")}
          onConfirm={() => {
            setShowCreateAccountModal(false);
            sessionStorage.setItem(
              "ob_form_state",
              JSON.stringify({
                debitValues,
                creditValues,
                date: date?.toISOString(),
              }),
            );
            navigate(
              "/v2/accounting/coa?create=true&name=Saldo+Awal&code=330999&category=CAPITAL&returnTo=/v2/accounting/coa/opening-balance",
            );
          }}
          onCancel={() => setShowCreateAccountModal(false)}
        />

        {/* Confirm unbalanced save */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={setShowConfirmModal}
          title={t("Save Changes")}
          description={t("debitCreditUnbalanceWarning", {
            difference,
            account: openingBalanceAccount?.name,
          })}
          confirmButtonText={t("common.yes")}
          cancelButtonText={t("common.cancel")}
          onConfirm={() => onSubmit(true)}
          loadingButton={submitLoading}
          disabledButton={submitLoading}
          type="warning"
        />
      </main>
    </AccountingWrapper>
  );
};

export default OpeningBalanceV2;
